import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { useOffer, useOfferMode } from "@nju/data-access-portal-offers";
import { getIdentityProviderLoginUrl } from "@nju/data-access-portal-user";
import { pushUserAccountEvent } from "@nju/data-layer";
import { redirectToOrchestratorState } from "@nju/feature-portal-orchestrator";
import { ErrorMessage, Input } from "@nju/form";
import { Icon } from "@nju/icons";
import { portalRoutes } from "@nju/routes";
import { A, BlockText, Button, Separator } from "@nju/ui";
import { getFormSessionStorageOrQuery, getFromQuery } from "@nju/utils";
import { loginMachine } from "./login.machine";

interface IQueryParams {
  offerId: string | undefined;
  flow: "ONBOARDING" | "multiOrder" | undefined;
  authCode: string | undefined;
  amount: string | undefined;
}

export function LoginForm() {
  const router = useRouter();
  const mode = useOfferMode();
  const [showPassword, setShowPassword] = React.useState(false);
  const resolveProperFlow = getFormSessionStorageOrQuery(
    "flow",
    router.query,
  ) as "ONBOARDING" | "multiOrder" | undefined;

  const { offerId, flow, authCode, amount }: IQueryParams =
    React.useMemo(() => {
      return {
        offerId: getFormSessionStorageOrQuery("oferta", router.query),
        flow: resolveProperFlow,
        authCode: getFromQuery("code", router.query),
        amount: getFromQuery("amount", router.query),
      };
    }, [resolveProperFlow, router.query]);

  const offer = useOffer(offerId);

  React.useLayoutEffect(() => {
    if (offer?.state === "ARCHIVED") {
      if (mode === "mobile") {
        router.push(portalRoutes.komorkowy.get());
      }

      if (mode === "ftth") {
        router.push(portalRoutes.swiatlowod.get());
      }
    }
  }, [mode, offer?.state, router]);

  const [{ context, tags }, send] = useMachine(loginMachine, {
    context: {
      authCode,
      flow: flow || undefined,
      offerIds: offerId ? [offerId] : undefined,
      mode,
    },
    actions: {
      onSubmit: (_, event) => {
        if (event.type !== "LOGIN_SUCCESSFUL") {
          return;
        }

        if (flow === "multiOrder" && ["mobile", "postpaid"].includes(mode)) {
          if (!event.orchestrator) {
            router.push(
              portalRoutes.wybierak.get({
                flow: "multiOrder",
                amount: amount || "1",
                offerId,
              }),
            );
            return;
          }

          if (
            event.orchestrator &&
            event.orchestrator.type === "NJU_MULTIORDER_ONBOARDING"
          ) {
            if (
              (event.orchestrator.offerCategory === "MOBILE_POSTPAID" &&
                mode !== "postpaid") ||
              (event.orchestrator.offerCategory !== "MOBILE_POSTPAID" &&
                mode !== "mobile")
            ) {
              router.push(portalRoutes.mojeKonto.index.get({ mode }));
            }

            if (event.orchestrator.state === "START") {
              router.push(
                portalRoutes.wybierak.get({
                  flow: "multiOrder",
                  amount: amount || "1",
                  offerId,
                }),
              );
              return;
            }

            if (event.orchestrator.state === "ITEM_SELECTION") {
              router.push(
                portalRoutes.czyZachowacNumer.get({
                  mode,
                  oferta: offerId,
                }),
              );
              return;
            }

            redirectToOrchestratorState({
              state: event.orchestrator.state,
              mode,
              flow: "multiOrder",
            });

            return;
          }
        }

        if (
          event.orchestrator &&
          event.orchestrator.type === "NJU_FTTH_ONBOARDING"
        ) {
          pushUserAccountEvent(event.orchestrator.userId);
          if (flow === "ONBOARDING") {
            redirectToOrchestratorState({
              state: event.orchestrator.state,
              mode,
            });

            return;
          } else {
            router.push(portalRoutes.mojeKonto.index.get());
            return;
          }
        } else if (event.user) {
          pushUserAccountEvent(event.user.id);
          router.push(portalRoutes.mojeKonto.index.get());
          return;
        }
      },
      onInvalidOrchestratorState: (_, event) => {
        if (event.type === "INVALID_ORCHESTRATOR_STATE") {
          router.push(portalRoutes.zaloguj.blad.get());
          return;
        }
      },
      onAnotherOrderInProgress: (_, event) => {
        if (event.type === "ANOTHER_ORDER_IN_PROGRESS") {
          router.push(portalRoutes.zamowienieWTrakcie.get());
          return;
        }
      },
    },
  });

  const isLoading = tags.has("loading");

  const handleSubmit: React.FormEventHandler<HTMLFormElement> =
    React.useCallback(
      (event) => {
        event.preventDefault();
        send({ type: "SUBMIT" });
      },
      [send],
    );

  const handleEmailChange: React.ChangeEventHandler<HTMLInputElement> =
    React.useCallback(
      ({ target }) => {
        send({ type: "SET_INPUT_VALUE", name: "email", value: target.value });
      },
      [send],
    );

  const handlePasswordChange: React.ChangeEventHandler<HTMLInputElement> =
    React.useCallback(
      ({ target }) => {
        send({
          type: "SET_INPUT_VALUE",
          name: "password",
          value: target.value,
        });
      },
      [send],
    );

  const storeDataInSessionStorage = React.useCallback(() => {
    if (offerId) {
      sessionStorage.setItem("oferta", offerId);
    }
    if (flow) {
      sessionStorage.setItem("flow", flow);
    }
  }, [offerId, flow]);

  return (
    <>
      <div className="flex gap-2">
        <div className="bg-porcelain rounded-tl-[15px] rounded-tr-[15px] flex flex-1 justify-center items-center h-[62px] from-tablet:h-[72px]">
          <Link
            className="text-size-16 from-tablet:text-size-20 text-center font-bold w-full h-full flex items-center justify-center"
            href="https://www.njumobile.pl/logowanie?backUrl=/mojekonto/formularz-kontaktowy/nowe-zgloszenie"
            rel="noreferrer"
          >
            <div className="w-[85px] from-tablet:w-[105px]">
              abonament na kartę
            </div>
          </Link>
        </div>
        <div className="rounded-tl-[15px] rounded-tr-[15px] bg-daintree text-white flex flex-1 justify-center items-center h-[62px] from-tablet:h-[72px]">
          <Link
            className="text-size-16 from-tablet:text-size-20 text-center font-bold w-full h-full flex items-center justify-center"
            href="#"
          >
            <div className="w-[85px] from-tablet:w-[105px]">
              subskrypcja światłowód
            </div>
          </Link>
        </div>
      </div>
      <div
        className={
          "p-4 px-3 border-t-0 border-b-[3px] border-x-[3px] border-porcelain rounded-bl-[15px] rounded-br-[15px] from-tablet:p-8"
        }
      >
        <div className="mb-6 flex gap-3">
          <Icon height={24} name="info-filled" tone="accent" width={24} />
          <div>
            <BlockText size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
              Logowanie do konta usług:
            </BlockText>
            <BlockText
              size={{ "@onlyMobile": "m", "@fromTablet": "l" }}
              weight="bold"
            >
              subskrypcji, światłowodu.
            </BlockText>
          </div>
        </div>
        <form autoComplete="false" onSubmit={handleSubmit}>
          <div className="mb-3" data-cy="login">
            <Input
              autoFocus={true}
              label="adres e-mail"
              labelBackground="white"
              name="email"
              onChange={handleEmailChange}
              state={context.emailError ? "error" : undefined}
              value={context.email}
            />
            {context.emailError && (
              <ErrorMessage message={context.emailError} />
            )}
          </div>
          <div className="mb-3" data-cy="password">
            <Input
              autoComplete="new-password"
              label="hasło"
              name="password-login"
              onChange={handlePasswordChange}
              state={context.passwordError ? "error" : undefined}
              suffix={
                <div
                  className="flex items-center"
                  data-cy="show-password-button"
                  onClick={() => {
                    setShowPassword((isVisible) => {
                      return !isVisible;
                    });
                  }}
                  role="button"
                >
                  <Icon
                    height={24}
                    name={showPassword ? "eye-slash" : "eye"}
                    width={30}
                  />
                </div>
              }
              type={showPassword ? "text" : "password"}
              value={context.password}
            />
            {context.passwordError && (
              <ErrorMessage message={context.passwordError} />
            )}
          </div>
          <div className="mt-4 mb-6 w-full">
            <Button isLoading={isLoading} size="login" type="submit">
              zaloguj się
            </Button>
          </div>
          <div className="mb-6">
            {context.formError && (
              <ErrorMessage iconAlign="start" message={context.formError} />
            )}
            {context.additionalFormError && (
              <div className="ml-6">
                <ErrorMessage
                  message={context.additionalFormError}
                  withIcon={false}
                />
              </div>
            )}
          </div>
          <div className="flex items-center justify-center mb-6">
            <Icon height={24} name="question-filled" width={24} />
            <Link
              className="ml-2 font-bold border-b-1 border-cerulean"
              href={portalRoutes.zmianaHasla.index.get()}
            >
              nie pamiętam hasła
            </Link>
          </div>
          <Separator size="xs" />
          <div className="mt-6">
            <BlockText align="center" size="l">
              lub zaloguj się przez
            </BlockText>
            <div
              className="flex mt-3 gap-2"
              onClick={storeDataInSessionStorage}
            >
              <Link
                className="w-full"
                href={getIdentityProviderLoginUrl(
                  "google",
                  portalRoutes.zaloguj.index.get(),
                  "portal",
                )}
                passHref={true}
              >
                <A data-cy="google-button" variant="google">
                  <Icon name="google" />
                </A>
              </Link>
              <Link
                className="w-full"
                href={getIdentityProviderLoginUrl(
                  "facebook",
                  portalRoutes.zaloguj.index.get(),
                  "portal",
                )}
                passHref={true}
              >
                <A data-cy="facebook-button" variant="facebook">
                  <Icon name="facebook" />
                </A>
              </Link>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}
